<template>
  <div
    class="ndsTag"
    :class="[`-${size}`, `-${color}`, `-${border}`]"
    data-testid="nds-tag"
    @click="emit('click', $event)"
  >
    <slot name="leftIcon">
      <NdsIcon
        v-if="leftIcon"
        class="ndsTag__icon"
        :color="getNdsTagIconColor"
        data-testid="nds-tag-left-icon"
        :icon="leftIcon"
        :size="16"
      />
    </slot>

    <slot name="label">
      <NdsText
        class="ndsTag__label"
        data-testid="nds-tag-label"
        emphasis
        :size="getLabelSize"
      >
        <NdsLineClamp
          :max-lines="1"
          :tooltip="label"
        >
          {{ label }}
        </NdsLineClamp>
      </NdsText>
    </slot>

    <slot name="rightIcon">
      <NdsIcon
        v-if="rightIcon"
        class="ndsTag__icon"
        :color="getNdsTagIconColor"
        data-testid="nds-tag-right-icon"
        :icon="rightIcon"
        :size="16"
      />
    </slot>
  </div>
</template>

<script lang="ts" setup>
import { computed, type PropType } from 'vue'

import NdsIcon from '@/components/NewDesignSystem/NdsIcon/NdsIcon.vue'

import { NdsColor } from '@/interfaces/NdsColors.interfaces'
import { NdsTagBorder, NdsTagColor, NdsTagSize } from '@/components/NewDesignSystem/NdsTag/NdsTag.interfaces'
import type { NdsIconType } from '@/components/NewDesignSystem/NdsIcon/NdsIcon.interfaces'
import NdsLineClamp from '@/components/NewDesignSystem/NdsLineClamp/NdsLineClamp.vue'
import NdsText from '@/components/NewDesignSystem/NdsText/NdsText.vue'
import { NdsTextSizes } from '@/components/NewDesignSystem/NdsText/NdsText.interfaces'

const emit = defineEmits<(e: 'click', event: MouseEvent) => void>()

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  size: {
    type: String as PropType<NdsTagSize>,
    default: NdsTagSize.DEFAULT,
  },
  color: {
    type: String as PropType<NdsTagColor>,
    default: NdsTagColor.NEUTRAL,
  },
  rightIcon: {
    type: String as PropType<NdsIconType>,
  },
  leftIcon: {
    type: String as PropType<NdsIconType>,
  },
  border: {
    type: String as PropType<NdsTagBorder>,
    default: NdsTagBorder.NONE,
  },
})

const getNdsTagIconColor = computed(() => {
  return (props.color === NdsTagColor.PRIMARY || props.color === NdsTagColor.SECONDARY) &&
    props.border !== NdsTagBorder.DASHED
    ? NdsColor.WHITE
    : NdsColor.NEUTRAL_950
})

const getLabelSize = computed(() => {
  if (props.size === NdsTagSize.COMPACT) {
    return NdsTextSizes.S
  } else if (props.size === NdsTagSize.BIG) {
    return NdsTextSizes.L
  } else {
    return NdsTextSizes.M
  }
})
</script>

<style lang="scss" scoped>
.ndsTag {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-0-75);

  border-radius: 4px;

  height: fit-content;
  width: fit-content;

  &__label {
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 20px;
    color: var(--neutral-950);
  }

  &__icon {
    flex-shrink: 0;
  }

  &.-compact {
    padding: var(--spacing-0-5) var(--spacing-0-75);

    .ndsTag__label {
      line-height: 16px;
    }
  }

  &.-default {
    padding: var(--spacing-1) var(--spacing-1-5);

    .ndsTag__label {
      line-height: 16px;
    }
  }

  &.-big {
    padding: var(--spacing-1) var(--spacing-1-5);
  }

  &.-neutral {
    border-color: var(--neutral-700);
    background: var(--neutral-200);
  }

  &.-primary {
    border-color: var(--primary-700);
    background: var(--primary-500);

    .ndsTag__label {
      color: white;
    }
  }

  &.-secondary {
    border-color: var(--secondary-700);
    background: var(--secondary-500);

    .ndsTag__label {
      color: white;
    }
  }

  &.-info {
    border-color: var(--info-700);
    background: var(--info-200);
  }

  &.-danger {
    border-color: var(--danger-700);
    background: var(--danger-200);
  }

  &.-success {
    border-color: var(--success-700);
    background: var(--success-100);
  }

  &.-primary-100 {
    border-color: var(--info-950);
    background: var(--primary-100);
  }

  &.-warning {
    border-color: var(--warning-400);
    background: var(--warning-200);
  }

  &.-empty {
    border-color: var(--neutral-300);
    background: transparent;
  }

  &.-borderSolid {
    border-style: solid;
    border-width: 1px;
  }

  &.-borderNone {
    border: none;
  }

  &.-borderDashed {
    border-style: dashed;
    border-width: 1px;

    background: transparent;

    .ndsTag__label {
      color: var(--neutral-950);
    }
  }
}
</style>
